import { dist2, idiv, imax, imin, withinRange } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, Enemy, Hero, SendaNpc } from '../types.ts';
import type { Pt, StoneRules, TrailBranch, TrailCrossing, TrailRules, TrailState, TrailStone } from '../caza-types.ts';
import { damageEnemy, ev, spawnEnemy } from './combat.ts';
import { orderMove } from './heroes.ts';
import { stepToward } from './enemies.ts';
import { howlCut, howlerHold, howlerOf, howlerTick } from './howler.ts';

/**
 * The Grey Wolf's tracks (docs/sendas/barbaro.md, chapter 1, acts 2 and 3; §4, «Las huellas»; the ground in
 * battle/caza-layout.ts). At each crossing the trail splits in two or three: one is the Wolf's, the others the pack's,
 * walked close by his on purpose. The clues read from up close (the drawing shows them within three hexes): where a
 * stream crosses, the pack jumps it and the Wolf, loaded, crosses on the stones; the pack stops to drink at the
 * puddles, the Wolf never; where the Wolf set the boar down to shift his grip, a pit and a drag mark. The hero follows a
 * branch by walking its prints: the Wolf's to its end is the crossing solved (the next one opens there); the pack's to
 * its end is an ambush of two wolves out of the rain (back to the crossing). Terrón, brought to the crossing (a tap on
 * its mark), sniffs three seconds and grunts toward the right one (on the ledge, a wolf comes after him each time).
 * The rain washes the crossings out in turn (each its bar): a washed one shows no prints; the last washed before the
 * end loses the hunt.
 *
 * The ledge (act 3, the test) adds the pack (a band at each crossing, a few more now and then: the danger goes with the
 * trail, not with the clock, so a slower reader meets no more of it), the crows and the howler (battle/howler.ts), and
 * the loose stones
 * at the cliff's foot: a tap sends the hero to one, a moment there and it rolls toward the sea striking what it meets
 * (the howler's howl is cut by it).
 *
 * Cost: the crossing at hand's branches (three, a dozen points each) against the hero once a tick; the pack a handful.
 */

const fresh = (r: TrailRules): TrailState => ({
  at: 0,
  prog: (r.crossings[0] as TrailCrossing).branches.map(() => -1),
  wrong: (r.crossings[0] as TrailCrossing).branches.map(() => false),
  solved: r.crossings.map(() => -1),
  sniff: -1,
  shown: [],
  sniffs: 0,
  job: -1,
  held: 0,
  stones: (r.stones ?? []).map((p) => ({ mode: 0, x: p.x, y: p.y, left: 0, struck: [] })),
  next: r.band?.first ?? 0,
  crow: r.crows?.first ?? 0,
  pack: [],
  fallen: [],
  ambushes: 0,
  rained: 0,
  done: -1,
  lost: false,
});

const terronOf = (s: BattleState): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === 'terron' && n.hp > 0);

/** Whether a crossing's tracks are washed out by now. */
export const trailWashed = (s: BattleState, r: TrailRules, i: number): boolean => s.waveT >= (r.wash[i] ?? 0x7fffffff);

/** Where the hero stands to push a stone (on the cliff's side of it, the sea before it). */
export const stoneStand = (p: Pt): Pt => ({ x: p.x - 650, y: p.y });

/** A tap on a mark: 0 the crossing at hand (Terrón to it), 1 + k the k-th loose stone. */
export const trailOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.trail;
  const w = s.senda?.trail;
  if (!r || !w || w.done >= 0 || w.lost) return;
  if (at === 0) {
    const c = r.crossings[w.at];
    if (!c) return;
    w.job = 0;
    w.held = 0;
    orderMove(s, h, c.at.x, c.at.y + 300);
  } else {
    const st = w.stones[at - 1];
    const p = r.stones?.[at - 1];
    if (!st || !p || st.mode !== 0) return;
    w.job = at;
    w.held = 0;
    const q = stoneStand(p);
    orderMove(s, h, q.x, q.y);
  }
  h.target = 0;
  h.forced = false;
};

/** How far along a branch the hero has walked: the furthest of its next few points within `near` of the hero. */
const follow = (r: TrailRules, b: TrailBranch, from: number, h: Hero): number => {
  let best = from;
  const hi = imin(b.pts.length - 1, from + r.slack);
  for (let i = imax(0, from - 1); i <= hi; i++) {
    const p = b.pts[i] as Pt;
    if (withinRange(p.x, p.y, h.x, h.y, r.near) && i > best) best = i;
  }
  return best;
};

/** The pack out of the rain: `n` wolves at a point, after the hero (the act's own list, for the howl). */
const wolves = (s: BattleState, r: TrailRules, w: TrailState, at: Pt, n: number): void => {
  for (let k = 0; k < n; k++) {
    const e = spawnEnemy(s, 'wolf', 1, false, 0, at.x + (k - 1) * 450, at.y + (k % 2) * 400, r.wolfHp);
    w.pack.push(e.id);
  }
};

/** A point out in the rain toward which the pack comes: off the hero ahead (north), or from the cliff's side. */
const outOf = (s: BattleState, h: Hero): Pt => {
  const R = s.map.radius - 3;
  const lim = R * 900;
  const k = rngInt(s.rng, 3);
  const x = k === 0 ? h.x - 6500 : k === 1 ? h.x + rngRange(s.rng, -2500, 2500) : h.x + 5500;
  const y = k === 1 ? h.y - 6500 : h.y + rngRange(s.rng, -3500, 1500);
  return { x: imax(-lim, imin(lim, x)), y: imax(-lim, imin(lim, y)) };
};

/** The crossing at hand solved (the Wolf's branch walked to its end): on to the next, or the end of the trail. */
const solve = (s: BattleState, r: TrailRules, w: TrailState, cue: (c: string) => void): void => {
  const c = r.crossings[w.at] as TrailCrossing;
  w.solved[w.at] = s.tick;
  ev(s, { t: 'senda', kind: 'cross', npc: '', x: c.at.x, y: c.at.y, n: w.at });
  w.at++;
  w.job = w.job === 0 ? -1 : w.job;
  w.sniff = -1;
  cue(`cross${w.at}`);
  if (w.at >= r.crossings.length) {
    w.done = s.tick;
    ev(s, { t: 'senda', kind: 'trailEnd', npc: '', x: r.end.x, y: r.end.y });
    return;
  }
  const next = r.crossings[w.at] as TrailCrossing;
  w.prog = next.branches.map(() => -1);
  w.wrong = next.branches.map(() => false);
  // On the ledge each crossing brings its band (the pack goes with the trail, not with the clock).
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (r.band && h) wolves(s, r, w, outOf(s, h), rngRange(s.rng, r.band.at[0], r.band.at[1]));
};

/** Terrón brought to the crossing at hand: three seconds of sniffing, then a grunt toward the Wolf's branch. */
const sniffTick = (s: BattleState, r: TrailRules, w: TrailState, h: Hero | undefined, cue: (c: string) => void): void => {
  const c = r.crossings[w.at];
  const t = terronOf(s);
  if (!c || !t) {
    w.sniff = -1;
    return;
  }
  if (w.shown.includes(w.at)) {
    if (w.job === 0) w.job = -1;
    return;
  }
  const by = (p: { x: number; y: number }): boolean => withinRange(p.x, p.y, c.at.x, c.at.y, r.reach);
  if (w.sniff < 0) {
    if (w.job !== 0 || !h) return;
    // Brought to it: he trots to the crossing's middle himself (following, he keeps his distance off the hero).
    if (!withinRange(t.x, t.y, c.at.x, c.at.y, 400)) {
      t.moving = true;
      stepToward(t, c.at.x, c.at.y, r.terronPace);
    }
    if (!by(h) || !by(t)) return;
    w.sniff = s.tick;
    w.sniffs++;
    ev(s, { t: 'senda', kind: 'sniff', npc: 'terron', x: t.x, y: t.y, n: w.at });
    // On the ledge the pack smells him at it.
    if (r.kind === 'brink' && r.terronWolves > 0) wolves(s, r, w, { x: t.x + 5500, y: t.y - 3000 }, r.terronWolves);
    return;
  }
  // A blow on him (or him walked off) and he starts again.
  if (t.flinch > 0 || !by(t)) {
    w.sniff = s.tick;
    return;
  }
  if (s.tick < w.sniff + r.sniff) return;
  w.sniff = -1;
  w.shown.push(w.at);
  w.job = -1;
  ev(s, { t: 'senda', kind: 'grunt', npc: 'terron', x: t.x, y: t.y, n: c.right });
  cue(`grunt${w.shown.length}`);
};

/** The loose stones: the one sent to, pushed after a moment by it; those rolling strike what they meet. */
const stonesTick = (s: BattleState, r: TrailRules, w: TrailState, h: Hero | undefined, cue: (c: string) => void): void => {
  const R = r.stone as StoneRules | undefined;
  if (!R || !r.stones) return;
  if (w.job > 0 && h) {
    const st = w.stones[w.job - 1] as TrailStone;
    const q = stoneStand(r.stones[w.job - 1] as Pt);
    if (st.mode !== 0) w.job = -1;
    else if (withinRange(h.x, h.y, q.x, q.y, R.reach) && !h.moving) {
      if (++w.held >= R.stand) {
        st.mode = 1;
        st.left = R.roll;
        w.job = -1;
        w.held = 0;
        ev(s, { t: 'senda', kind: 'stonePush', npc: '', x: st.x, y: st.y, n: w.stones.indexOf(st) });
      }
    } else w.held = 0;
  }
  for (const st of w.stones) {
    if (st.mode !== 1) continue;
    const dx = imin(R.speed, st.left);
    st.x += dx;
    st.left -= dx;
    for (const e of s.enemies) {
      if (e.hp <= 0 || st.struck.includes(e.id) || !withinRange(e.x, e.y, st.x, st.y, R.width)) continue;
      st.struck.push(e.id);
      const was = s.senda?.howler;
      const howling = !!was && was.id === e.id && (was.phase === 1 || was.phase === 2);
      damageEnemy(s, e, R.hit, { pid: h?.pid ?? '', ignoresArmor: true });
      ev(s, { t: 'senda', kind: 'stoneHit', npc: '', x: e.x, y: e.y, n: howling ? 1 : 0 });
      if (howling) {
        howlCut(s, e, cue);
        cue('stone1');
      } else if (e.hp > 0) e.stun = imax(e.stun, R.stun);
    }
    if (st.left <= 0) st.mode = 2;
  }
};

export const trailTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.trail;
  const st = s.senda;
  if (!r || !st) return;
  const w = (st.trail ??= fresh(r));
  if (!s.started || w.done >= 0 || w.lost) return;
  const h = s.heroes.find((x) => x.downTicks === 0);
  // The rain: each crossing washed in turn; the last before the end loses it.
  while (w.rained < r.crossings.length && trailWashed(s, r, w.rained)) {
    const i = w.rained++;
    const c = r.crossings[i] as TrailCrossing;
    ev(s, { t: 'senda', kind: 'washed', npc: '', x: c.at.x, y: c.at.y, n: i });
    if (i >= w.at) cue('washed1');
  }
  if (trailWashed(s, r, r.crossings.length - 1)) {
    w.lost = true;
    return;
  }
  // The pack fallen (for the howl), the pack gone.
  let k = 0;
  for (const id of w.pack) {
    const e = s.enemies.find((x) => x.id === id);
    if (!e || e.hp <= 0) {
      if (e) w.fallen.push({ x: e.x, y: e.y });
      if (w.fallen.length > 6) w.fallen.shift();
      continue;
    }
    w.pack[k++] = id;
  }
  w.pack.length = k;
  const c = r.crossings[w.at] as TrailCrossing;
  if (h) {
    // The branches followed; their clues seen up close (Bera's lines); the ends reached.
    for (let b = 0; b < c.branches.length; b++) {
      const br = c.branches[b] as TrailBranch;
      const was = w.prog[b] as number;
      const now = follow(r, br, was, h);
      if (now !== was) w.prog[b] = now;
      if (!trailWashed(s, r, w.at)) {
        const see = (i: number): boolean => i >= 0 && withinRange((br.pts[i] as Pt).x, (br.pts[i] as Pt).y, h.x, h.y, r.see);
        if (br.jump && see(br.stream)) cue('jump');
        if (br.drink && see(br.puddle)) cue('drink');
        if (see(br.pit)) cue('pit');
      }
      if (now < br.pts.length - 1) continue;
      if (br.right) {
        solve(s, r, w, cue);
        break;
      }
      if (!w.wrong[b]) {
        w.wrong[b] = true;
        w.ambushes++;
        const end = br.pts[br.pts.length - 1] as Pt;
        ev(s, { t: 'senda', kind: 'wrong', npc: '', x: end.x, y: end.y, n: b });
        cue(`wrong${w.ambushes}`);
        // Out of the rain, either side of the end.
        const ax = end.x + (end.x > c.at.x ? 1800 : -1800);
        wolves(s, r, w, { x: ax, y: end.y - 1200 }, r.ambush);
      }
    }
    if (w.done >= 0) return;
  }
  sniffTick(s, r, w, h, cue);
  stonesTick(s, r, w, h, cue);
  howlerTick(s, r, w, cue);
  // The ledge's pack and crows.
  if (h && r.band && s.waveT >= w.next) {
    const first = w.next === r.band.first;
    w.next = s.waveT + rngRange(s.rng, r.band.every[0], r.band.every[1]);
    const n = first ? rngRange(s.rng, r.band.at[0], r.band.at[1]) : rngRange(s.rng, r.band.n[0], r.band.n[1]);
    wolves(s, r, w, outOf(s, h), n);
  }
  if (h && r.crows && s.waveT >= w.crow) {
    w.crow = s.waveT + r.crows.every;
    for (let n = 0; n < r.crows.n; n++) spawnEnemy(s, 'crow', 1, false, 0, h.x + 7000 + n * 400, h.y - 2000 + n * 600, imax(1, idiv(r.wolfHp, 3)));
  }
};

/** The howler's own way (battle/howler.ts); the rest of the pack hunts as any pack does. */
export const trailHold = (s: BattleState, e: Enemy, px: (e: Enemy) => number): boolean => howlerHold(s, e, px);

/** The crossings solved, of all. */
export const trailGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.trail;
  const w = s.senda?.trail;
  if (!r || !w) return { done: 0, need: 1 };
  return { done: w.at, need: r.crossings.length };
};

/** Won at the end of the trail; lost to the rain. */
export const trailResult = (s: BattleState): 'won' | 'lost' | null => {
  const w = s.senda?.trail;
  if (!w) return null;
  if (w.lost) return 'lost';
  if (w.done >= 0 && s.tick >= w.done + 18) return 'won';
  return null;
};

/** The crossing at hand's branch nearest a point (-1: none within `r`): the bot's and the HUD's. */
export const branchNear = (c: TrailCrossing, x: number, y: number, r: number): number => {
  let best = -1;
  let bd = r * r;
  c.branches.forEach((b, i) => {
    for (const p of b.pts) {
      const d = dist2(p.x, p.y, x, y);
      if (d < bd) {
        bd = d;
        best = i;
      }
    }
  });
  return best;
};

export { howlerOf };
